// ReviewAdjustStep — Client Review & Adjustment Component
// Allows controlled client input while preserving engineering integrity

import React, { useState } from 'react';
import { EquipmentItem, RuleEngineOutput } from './ruleEngine';
import { Shield, Lock, Plus, AlertTriangle, CheckCircle2, Edit3, Flag, X } from 'lucide-react';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type EquipmentStatus = 'included' | 'request-removal';

export interface EquipmentAdjustment {
  name: string;
  specification: string;
  category: string;
  status: EquipmentStatus;
  removalReason: string;
  isLocked: boolean;
}

export interface AssumptionAdjustment {
  id: string;
  text: string;
  status: 'accept' | 'modify' | 'flag';
  modifiedText: string;
}

export interface AdditionRequest {
  id: string;
  description: string;
  justification: string;
}

export interface ClientAdjustments {
  equipment: EquipmentAdjustment[];
  assumptions: AssumptionAdjustment[];
  additions: AdditionRequest[];
}

// Default assumptions based on operational context
export function getDefaultAssumptions(ruleOutput: RuleEngineOutput, h2s: string, env: string): AssumptionAdjustment[] {
  const assumptions: AssumptionAdjustment[] = [
    { id: 'a1', text: 'Final equipment specification subject to receipt of detailed well data and completion program', status: 'accept', modifiedText: '' },
    { id: 'a2', text: 'Mobilization timeline assumes equipment availability at time of confirmation', status: 'accept', modifiedText: '' },
    { id: 'a3', text: 'Site access, crane availability, and laydown area to be confirmed by client', status: 'accept', modifiedText: '' },
    { id: 'a4', text: 'All certifications and third-party inspections current at time of mobilization', status: 'accept', modifiedText: '' },
  ];
  if (h2s === 'yes') {
    assumptions.push({ id: 'a5', text: 'All personnel H₂S-certified and SCBA-trained prior to site mobilization', status: 'accept', modifiedText: '' });
  }
  if (env === 'offshore') {
    assumptions.push({ id: 'a6', text: 'Vessel/barge availability and deck load capacity confirmed by client', status: 'accept', modifiedText: '' });
  }
  if (ruleOutput.flags.length > 0) {
    assumptions.push({ id: 'a7', text: 'Engineering flags require client confirmation before final design', status: 'accept', modifiedText: '' });
  }
  return assumptions;
}

// Determine if an equipment item is locked (critical safety/engineering constraint)
export function isEquipmentLocked(item: EquipmentItem, h2s: string, pressure: number): boolean {
  // Safety systems are always locked
  if (item.category === 'safety') return true;
  // H2S-related items locked when H2S present
  if (h2s === 'yes' && (item.name.includes('Safety') || item.name.includes('HSE') || item.name.includes('NACE') || item.name.includes('Special Materials'))) return true;
  // Pressure-rated primary items locked when pressure exceeds threshold
  if (pressure > 5000 && item.name === 'Choke Manifold') return true;
  return false;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

interface ReviewAdjustStepProps {
  ruleOutput: RuleEngineOutput;
  adjustments: ClientAdjustments;
  onAdjustmentsChange: (adj: ClientAdjustments) => void;
  h2sPresence: string;
  wellheadPressure: string;
}

export const ReviewAdjustStep: React.FC<ReviewAdjustStepProps> = ({
  ruleOutput,
  adjustments,
  onAdjustmentsChange,
  h2sPresence,
  wellheadPressure,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddition, setNewAddition] = useState({ description: '', justification: '' });
  const pressure = parseFloat(wellheadPressure) || 0;

  const updateEquipment = (index: number, update: Partial<EquipmentAdjustment>) => {
    const updated = [...adjustments.equipment];
    updated[index] = { ...updated[index], ...update };
    onAdjustmentsChange({ ...adjustments, equipment: updated });
  };

  const updateAssumption = (index: number, update: Partial<AssumptionAdjustment>) => {
    const updated = [...adjustments.assumptions];
    updated[index] = { ...updated[index], ...update };
    onAdjustmentsChange({ ...adjustments, assumptions: updated });
  };

  const addEquipmentRequest = () => {
    if (!newAddition.description.trim()) return;
    const addition: AdditionRequest = {
      id: `add-${Date.now()}`,
      description: newAddition.description,
      justification: newAddition.justification,
    };
    onAdjustmentsChange({ ...adjustments, additions: [...adjustments.additions, addition] });
    setNewAddition({ description: '', justification: '' });
    setShowAddForm(false);
  };

  const removeAddition = (id: string) => {
    onAdjustmentsChange({ ...adjustments, additions: adjustments.additions.filter(a => a.id !== id) });
  };

  // Count changes for summary
  const removalRequests = adjustments.equipment.filter(e => e.status === 'request-removal').length;
  const modifiedAssumptions = adjustments.assumptions.filter(a => a.status === 'modify').length;
  const flaggedAssumptions = adjustments.assumptions.filter(a => a.status === 'flag').length;
  const totalChanges = removalRequests + modifiedAssumptions + flaggedAssumptions + adjustments.additions.length;

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4">
        <p className="text-slate-300 text-sm leading-relaxed">
          Review the recommended configuration below. You may request adjustments — all changes are subject to engineering validation before final confirmation.
        </p>
      </div>

      {/* ─── EQUIPMENT REVIEW ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-['Oswald'] text-lg text-white uppercase tracking-wide">Equipment Configuration</h3>
          <span className="text-slate-500 text-sm">({adjustments.equipment.length} items)</span>
        </div>

        <div className="space-y-2">
          {adjustments.equipment.map((item, idx) => (
            <div
              key={idx}
              className={`rounded-lg border p-4 transition-all ${
                item.isLocked
                  ? 'bg-slate-800/80 border-slate-600'
                  : item.status === 'request-removal'
                  ? 'bg-red-500/5 border-red-500/30'
                  : 'bg-slate-800/50 border-slate-700/50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {item.isLocked && <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    <span className="text-white text-sm font-medium">{item.name}</span>
                    <span className="text-slate-500 text-xs px-2 py-0.5 bg-slate-700/50 rounded">{item.category}</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-1">{item.specification}</p>
                </div>

                {/* Controls */}
                {item.isLocked ? (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg shrink-0">
                    <Shield className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-amber-300 text-xs font-medium">Required</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => updateEquipment(idx, { status: 'included', removalReason: '' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        item.status === 'included'
                          ? 'bg-green-500/10 border border-green-500/40 text-green-300'
                          : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                      }`}
                    >
                      Include
                    </button>
                    <button
                      onClick={() => updateEquipment(idx, { status: 'request-removal' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        item.status === 'request-removal'
                          ? 'bg-red-500/10 border border-red-500/40 text-red-300'
                          : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                      }`}
                    >
                      Request Removal
                    </button>
                  </div>
                )}
              </div>

              {/* Locked message */}
              {item.isLocked && (
                <p className="text-amber-400/70 text-xs mt-2 italic">
                  This item is required based on operational parameters and cannot be removed.
                </p>
              )}

              {/* Removal reason input */}
              {item.status === 'request-removal' && !item.isLocked && (
                <div className="mt-3 pt-3 border-t border-red-500/20">
                  <label className="text-red-300 text-xs font-medium block mb-1.5">Reason for removal request *</label>
                  <input
                    type="text"
                    value={item.removalReason}
                    onChange={e => updateEquipment(idx, { removalReason: e.target.value })}
                    placeholder="e.g., Client will supply own equipment, Not required for this scope..."
                    className="w-full bg-slate-900/50 border border-red-500/30 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-red-400"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Equipment Request */}
        <div className="mt-4">
          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-dashed border-slate-600 rounded-lg text-slate-400 text-sm hover:border-orange-500/50 hover:text-orange-300 transition-all w-full justify-center"
            >
              <Plus className="w-4 h-4" /> Request Additional Equipment
            </button>
          ) : (
            <div className="bg-slate-800/50 border border-orange-500/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-orange-300 text-sm font-medium">Request Additional Equipment</span>
                <button onClick={() => setShowAddForm(false)} className="text-slate-500 hover:text-slate-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={newAddition.description}
                onChange={e => setNewAddition({ ...newAddition, description: e.target.value })}
                placeholder="Equipment name and specification..."
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
              />
              <input
                type="text"
                value={newAddition.justification}
                onChange={e => setNewAddition({ ...newAddition, justification: e.target.value })}
                placeholder="Justification / reason for addition..."
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
              />
              <button
                onClick={addEquipmentRequest}
                disabled={!newAddition.description.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Request
              </button>
            </div>
          )}
        </div>

        {/* Show additions */}
        {adjustments.additions.length > 0 && (
          <div className="mt-3 space-y-2">
            <span className="text-orange-300 text-xs font-semibold uppercase tracking-wider">Requested Additions</span>
            {adjustments.additions.map(add => (
              <div key={add.id} className="flex items-center justify-between bg-orange-500/5 border border-orange-500/20 rounded-lg px-4 py-3">
                <div>
                  <p className="text-white text-sm">{add.description}</p>
                  {add.justification && <p className="text-slate-400 text-xs mt-0.5">{add.justification}</p>}
                </div>
                <button onClick={() => removeAddition(add.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── ASSUMPTIONS REVIEW ───────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-['Oswald'] text-lg text-white uppercase tracking-wide">Assumptions & Conditions</h3>
        </div>

        <div className="space-y-3">
          {adjustments.assumptions.map((assumption, idx) => (
            <div
              key={assumption.id}
              className={`rounded-lg border p-4 transition-all ${
                assumption.status === 'flag'
                  ? 'bg-amber-500/5 border-amber-500/30'
                  : assumption.status === 'modify'
                  ? 'bg-blue-500/5 border-blue-500/30'
                  : 'bg-slate-800/50 border-slate-700/50'
              }`}
            >
              <p className="text-slate-300 text-sm mb-3">{assumption.text}</p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateAssumption(idx, { status: 'accept', modifiedText: '' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    assumption.status === 'accept'
                      ? 'bg-green-500/10 border border-green-500/40 text-green-300'
                      : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <CheckCircle2 className="w-3 h-3" /> Accept
                </button>
                <button
                  onClick={() => updateAssumption(idx, { status: 'modify' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    assumption.status === 'modify'
                      ? 'bg-blue-500/10 border border-blue-500/40 text-blue-300'
                      : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <Edit3 className="w-3 h-3" /> Modify
                </button>
                <button
                  onClick={() => updateAssumption(idx, { status: 'flag', modifiedText: '' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    assumption.status === 'flag'
                      ? 'bg-amber-500/10 border border-amber-500/40 text-amber-300'
                      : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <Flag className="w-3 h-3" /> Flag Issue
                </button>
              </div>

              {/* Modify input */}
              {assumption.status === 'modify' && (
                <div className="mt-3 pt-3 border-t border-blue-500/20">
                  <label className="text-blue-300 text-xs font-medium block mb-1.5">Your modification</label>
                  <textarea
                    value={assumption.modifiedText}
                    onChange={e => updateAssumption(idx, { modifiedText: e.target.value })}
                    placeholder="Describe the modification or alternative..."
                    rows={2}
                    className="w-full bg-slate-900/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-blue-400 resize-none"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ─── ADJUSTMENT SUMMARY ───────────────────────────────────────── */}
      {totalChanges > 0 && (
        <div className="bg-slate-800/80 border border-orange-500/30 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <h4 className="text-orange-300 text-sm font-semibold uppercase tracking-wide">Client Adjustment Summary</h4>
          </div>
          <div className="space-y-2 text-sm">
            {removalRequests > 0 && (
              <p className="text-slate-300">
                <span className="text-red-400 font-medium">{removalRequests}</span> equipment removal request{removalRequests > 1 ? 's' : ''}
              </p>
            )}
            {adjustments.additions.length > 0 && (
              <p className="text-slate-300">
                <span className="text-orange-400 font-medium">{adjustments.additions.length}</span> equipment addition request{adjustments.additions.length > 1 ? 's' : ''}
              </p>
            )}
            {modifiedAssumptions > 0 && (
              <p className="text-slate-300">
                <span className="text-blue-400 font-medium">{modifiedAssumptions}</span> assumption{modifiedAssumptions > 1 ? 's' : ''} modified
              </p>
            )}
            {flaggedAssumptions > 0 && (
              <p className="text-slate-300">
                <span className="text-amber-400 font-medium">{flaggedAssumptions}</span> assumption{flaggedAssumptions > 1 ? 's' : ''} flagged for discussion
              </p>
            )}
          </div>
          <p className="text-slate-500 text-xs mt-3 italic">
            All adjustments are subject to engineering validation. Final configuration will be confirmed by EnergiPro engineering team.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── EXPORT SUMMARY BUILDER ──────────────────────────────────────────────────

export function buildAdjustmentSummaryText(adjustments: ClientAdjustments): string {
  const lines: string[] = [];

  const removals = adjustments.equipment.filter(e => e.status === 'request-removal');
  const mods = adjustments.assumptions.filter(a => a.status === 'modify');
  const flags = adjustments.assumptions.filter(a => a.status === 'flag');

  if (removals.length === 0 && adjustments.additions.length === 0 && mods.length === 0 && flags.length === 0) {
    return 'No client adjustments requested. Original configuration accepted as-is.';
  }

  lines.push('CLIENT-REQUESTED ADJUSTMENTS');
  lines.push('(Subject to engineering validation)');
  lines.push('');

  if (removals.length > 0) {
    lines.push('REMOVAL REQUESTS:');
    removals.forEach(r => {
      lines.push(`- ${r.name} (${r.specification}) — Reason: ${r.removalReason || 'Not specified'}`);
    });
    lines.push('');
  }

  if (adjustments.additions.length > 0) {
    lines.push('ADDITION REQUESTS:');
    adjustments.additions.forEach(a => {
      lines.push(`- ${a.description}${a.justification ? ` — ${a.justification}` : ''}`);
    });
    lines.push('');
  }

  if (mods.length > 0) {
    lines.push('MODIFIED ASSUMPTIONS:');
    mods.forEach(m => {
      lines.push(`- Original: ${m.text}`);
      lines.push(`  Client modification: ${m.modifiedText || 'Not specified'}`);
    });
    lines.push('');
  }

  if (flags.length > 0) {
    lines.push('FLAGGED FOR DISCUSSION:');
    flags.forEach(f => {
      lines.push(`- ${f.text}`);
    });
    lines.push('');
  }

  lines.push('NOTE: Above adjustments do not override engineering constraints.');
  lines.push('Final configuration subject to EnergiPro engineering review and confirmation.');

  return lines.join('\n');
}
